import React, { useState, useRef, useEffect, useCallback } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { Modal } from 'bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { MultiSelect } from 'primereact/multiselect';
import { Toast } from "primereact/toast";
import userDetailsStyles from "../../../styles/userDetails/userDetails.module.css";
import addUserDetailsClient from '../../../utility/features/api_clients/userDetails/addUserDetailsClient';
import getBranchesList from '../../../utility/features/api_clients/reports/getBranchesList';


function AddUserModal({ setIsUserAdded, setLoading, setSuccessMessage, setErrorMessage, openSuccessModal, openFailureModal }) {

    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        userName: "",
        email: "",
        password: "",
        confirmPassword: "",
        branchID: []
    });
    const addUserFormRef = useRef(null);
    const loadingImageRef = useRef(null);
    const successToastRef = useRef(null);
    const errorToastRef = useRef(null);
    const [selectedBranch, setSelectedBranch] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loadingBranches, setLoadingBranches] = useState(true);

    const cleanupModalArtifacts = () => {
        const openModals = document.querySelectorAll('.modal.show');
        if (openModals.length > 0) return;

        document.body.classList.remove('modal-open');
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('padding-right');
        if ((document.body.getAttribute('style') || '').trim() === '') {
            document.body.removeAttribute('style');
        }
        document.querySelectorAll('.modal-backdrop').forEach((bd) => bd.remove());
    };

    useEffect(() => {
        const modalEl = document.getElementById('addUserModal');
        if (!modalEl) return;
        const handleHidden = () => {
            cleanupModalArtifacts();
        };
        modalEl.addEventListener('hidden.bs.modal', handleHidden);
        return () => {
            modalEl.removeEventListener('hidden.bs.modal', handleHidden);
        };
    }, []);
    const [validationErrors, setValidationErrors] = useState({});
    const [touchedFields, setTouchedFields] = useState({});

    // Memoized callbacks for MultiSelect to prevent re-renders
    const optionLabelCallback = useCallback((option) => 
        `${option.branchID || option.id}${option.branchName ? ' - ' + option.branchName : ''}`,
        []
    );

    const optionValueCallback = useCallback((option) => 
        option.branchID || option.id,
        []
    );

    const multiSelectChangeCallback = useCallback((e) => {
        setSelectedBranch(e.value);
        handleForm(null, "branchID", e.value.length > 0 ? e.value : []);
    }, []);

    const showSuccessToast = (detail) => {
        successToastRef.current?.show({ severity: "success", summary: "Success", detail, life: 10000 });
    };

    const showErrorToast = (detail) => {
        errorToastRef.current?.show({ severity: "error", summary: "Error", detail, life: 5000 });
    };

    // Fetch branches on component mount
    useEffect(() => {
        const fetchBranches = async () => {
            try {
                setLoadingBranches(true);
                const response = await getBranchesList();
                if (response && !response.error && response.data) {
                    setBranches(response.data);
                } else {
                    console.error('Failed to fetch branches:', response?.message);
                    showErrorToast(response?.data?.message || response?.message || 'Failed to load branches');
                    setBranches([]);
                }
            } catch (error) {
                console.error('Error fetching branches:', error);
                showErrorToast(error?.message || 'Failed to load branches');
                setBranches([]);
            } finally {
                setLoadingBranches(false);
            }
        };

        fetchBranches();
    }, []);

    const handleForm = (e = null, type, value = null) => {
        let val = value === null ? e.target.value : value;
        setForm(prevForm => ({
            ...prevForm,
            [type]: val
        }))
        // Clear validation error for this field when user types
        if (validationErrors[type]) {
            setValidationErrors(prev => ({ ...prev, [type]: null }));
        }
        
        // Map form field names to validation field classes
        const fieldClassMap = {
            firstName: 'fname',
            lastName: 'lname',
            userName: 'userName',
            email: 'email',
            password: 'password',
            confirmPassword: 'confirmPassword',
            branchID: 'branch'
        };
        
        // Hide validation errors when user starts typing
        const fieldClass = fieldClassMap[type];
        if (fieldClass && addUserFormRef.current) {
            hideAllValidationErrors(fieldClass);
        }
    }

    const showValidationError = (fieldClass, errorType) => {
        const errorElement = addUserFormRef.current?.querySelector(`.invalid-${fieldClass}-${errorType}`);
        if (errorElement) {
            errorElement.classList.add("show");
        }
    }

    const hideAllValidationErrors = (fieldClass) => {
        const errorElements = addUserFormRef.current?.querySelectorAll(`[class*="invalid-${fieldClass}"]`);
        errorElements?.forEach(el => el.classList.remove("show"));
    }

    const validateField = (fieldClass, value, validations) => {
        hideAllValidationErrors(fieldClass);
        
        for (const validation of validations) {
            if (!validation.condition(value)) {
                showValidationError(fieldClass, validation.errorType);
                return false;
            }
        }
        return true;
    }

    const handleBlur = (fieldName) => {
        // Mark field as touched
        setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
        
        // Validation mapping
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        
        const fieldValidations = {
            firstName: {
                fieldClass: 'fname',
                value: form.firstName,
                validations: [{ condition: (val) => val.trim() !== '', errorType: 'req' }]
            },
            lastName: {
                fieldClass: 'lname',
                value: form.lastName,
                validations: [{ condition: (val) => val.trim() !== '', errorType: 'req' }]
            },
            userName: {
                fieldClass: 'userName',
                value: form.userName,
                validations: [{ condition: (val) => val.trim() !== '', errorType: 'req' }]
            },
            email: {
                fieldClass: 'email',
                value: form.email,
                validations: [
                    { condition: (val) => val.trim() !== '', errorType: 'req' },
                    { condition: (val) => emailRegex.test(val.trim()), errorType: 'other' }
                ]
            },
            password: {
                fieldClass: 'password',
                value: form.password,
                validations: [{ condition: (val) => val.trim() !== '', errorType: 'req' }]
            },
            confirmPassword: {
                fieldClass: 'confirmPassword',
                value: form.confirmPassword,
                validations: [
                    { condition: (val) => val.trim() !== '', errorType: 'req' },
                    { condition: (val) => val === form.password, errorType: 'mismatch' }
                ]
            },
            branchID: {
                fieldClass: 'branch',
                value: form.branchID,
                validations: [{ condition: (val) => val.length > 0 && val !== '' && val !== null, errorType: 'req' }]
            }
        };
        
        const validation = fieldValidations[fieldName];
        if (validation) {
            if (fieldName === 'confirmPassword') {
                // Special handling for confirm password
                hideAllValidationErrors(validation.fieldClass);
                if (validation.value.trim() === '') {
                    showValidationError(validation.fieldClass, 'req');
                } else if (validation.value !== form.password) {
                    showValidationError(validation.fieldClass, 'mismatch');
                }
            } else {
                validateField(validation.fieldClass, validation.value, validation.validations);
            }
        }
    }

    const addUserFormSubmit = async (event) => {
        event.preventDefault();
        const fnameInput = addUserFormRef.current.querySelector("#fname");
        const lnameInput = addUserFormRef.current.querySelector("#lname");
        const userNameInput = addUserFormRef.current.querySelector("#userName");
        const emailInput = addUserFormRef.current.querySelector("#email");
        const passwordInput = addUserFormRef.current.querySelector("#password");
        const confirmPasswordInput = addUserFormRef.current.querySelector("#confirmPassword");

        // Validation rules
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        
        const is_valid_fname = validateField("fname", fnameInput.value, [
            { condition: (val) => val.trim() !== '', errorType: 'req' }
        ]);

        const is_valid_lname = validateField("lname", lnameInput.value, [
            { condition: (val) => val.trim() !== '', errorType: 'req' }
        ]);

        const is_valid_userName = validateField("userName", userNameInput.value, [
            { condition: (val) => val.trim() !== '', errorType: 'req' }
        ]);

        const is_valid_email = validateField("email", emailInput.value, [
            { condition: (val) => val.trim() !== '', errorType: 'req' },
            { condition: (val) => emailRegex.test(val.trim()), errorType: 'other' }
        ]);

        const is_valid_password = validateField("password", passwordInput.value, [
            { condition: (val) => val.trim() !== '', errorType: 'req' }
        ]);

        let is_valid_confirmPassword = true;
        hideAllValidationErrors("confirmPassword");
        if (confirmPasswordInput.value.trim() === '') {
            showValidationError("confirmPassword", "req");
            is_valid_confirmPassword = false;
        } else if (confirmPasswordInput.value !== passwordInput.value) {
            showValidationError("confirmPassword", "mismatch");
            is_valid_confirmPassword = false;
        }

        const is_valid_branch = validateField("branch", form.branchID, [
            { condition: (val) => val.length > 0 && val !== '' && val !== null, errorType: 'req' }
        ]);

        if (!is_valid_fname || !is_valid_lname || !is_valid_userName || !is_valid_email || 
            !is_valid_password || !is_valid_confirmPassword || !is_valid_branch) {
            return false;
        }

        const newUser = {
            UserName: userNameInput.value.trim(),
            firstName: fnameInput.value.trim(),
            lastName: lnameInput.value.trim(),
            email: emailInput.value.trim(),
            password: passwordInput.value.trim(),
            roleID: 2,
            branchIDs: form.branchID
        };

        setLoading(true);
        try {
            const result = await addUserDetailsClient(newUser);            
            if (result.status === 200 && result.data.messageCode === 200) {
                setIsUserAdded(true);
                setErrorMessage('');
                showSuccessToast("User added successfully!");
                setSuccessMessage("User added successfully!");
                openSuccessModal?.();
                handleResetForm();
                
                setTimeout(() => {
                    const el = document.getElementById('addUserModal');
                    if (el) {
                        const instance = Modal.getOrCreateInstance(el);
                        instance.hide();
                    }
                }, 100);

                // Clean up modal artifacts
                document.querySelectorAll('.modal.show').forEach((el) => {
                    const instance = Modal.getOrCreateInstance(el);
                    instance.hide();
                });

                cleanupModalArtifacts();
            } else if (result.data.messageCode === 20000) {
                setSuccessMessage("");
                showErrorToast(result?.data?.message);
                setErrorMessage(result?.data?.message);
            } else {
                const errorMsg = result?.data?.message || result?.message || "Failed to add user";
                showErrorToast("Action failed please try again !");
                setErrorMessage(errorMsg);
                cleanupModalArtifacts();
            }
        } catch (error) {
            console.error("Error adding user:", error);
            const errorMsg = error?.message || "An error occurred while adding the user";
            showErrorToast( "An error occurred while adding the user");
            setErrorMessage(errorMsg);
            cleanupModalArtifacts();
        } finally {
            setLoading(false);
        }
    }

    const handleResetForm = () => {
        setForm({
            firstName: "",
            lastName: "",
            userName: "",
            email: "",
            password: "",
            confirmPassword: "",
            branchID: []
        });
        setSelectedBranch([]);
        setValidationErrors({});
        setTouchedFields({});
        
        if (addUserFormRef.current) {
            addUserFormRef.current.reset();
        }
        
        if (loadingImageRef.current) {
            loadingImageRef.current.style.display = "none";
        }

        // Hide all validation errors
        const fieldClasses = ['fname', 'lname', 'userName', 'email', 'password', 'confirmPassword', 'branch'];
        fieldClasses.forEach(fieldClass => {
            if (addUserFormRef.current) {
                hideAllValidationErrors(fieldClass);
            }
        });
    }
    
    return (
        <>
          <Toast ref={successToastRef} position="top-right" />
            <div className={`modal fade ${userDetailsStyles["userDetailsModal"]}`} id="addUserModal" tabIndex="-1" aria-labelledby="addUserModalLabel" aria-hidden="true">
                <div className={`modal-dialog ${userDetailsStyles["modal-dialog"]}`}>
                    <div className={`modal-content ${userDetailsStyles["modal-content"]}`}>
                        <div className={`modal-header ${userDetailsStyles["modal-header"]}`}>
                            <h5 id="addUserModalLabel" className={`modal-title ${userDetailsStyles["modal-title"]}`}>Add User Details</h5>
                            <button type="button" className={`btn-close ${userDetailsStyles["btn-close"]}`} data-bs-dismiss="modal" aria-label="Close" onClick={() => handleResetForm()} />
                        </div>
                        <Toast ref={errorToastRef} position="top-right" />
                        <div className={`modal-body ${userDetailsStyles["modal-body"]}`}>
                            <p><span>* Required information</span></p>
                            <form autoComplete="off" ref={addUserFormRef} className={`${userDetailsStyles["lead-form"]}`} noValidate id="addUserModalForm" name="addUserModal">
                                <div className="form-wrapper">
                                    {/* First Name and Last Name Row */}
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="fname" className={`form-label ${userDetailsStyles["form-label"]}`}>First Name*</label>
                                            <input 
                                                type="text" 
                                                className={`form-control`} 
                                                placeholder="Enter firstname" 
                                                name="fname" 
                                                id="fname" 
                                                required 
                                                value={form.firstName} 
                                                onChange={(e) => handleForm(e, "firstName")} 
                                                onBlur={() => handleBlur("firstName")}
                                            />
                                            <div className={`invalid-fname-req invalid-feedback ${userDetailsStyles["invalid-feedback"]}`}>First Name is required</div>
                                            <div className={`invalid-fname-other invalid-feedback ${userDetailsStyles["invalid-feedback"]}`}>Invalid first name</div>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="lname" className={`form-label ${userDetailsStyles["form-label"]}`}>Last Name*</label>
                                            <input 
                                                type="text" 
                                                className={`form-control `} 
                                                placeholder="Enter lastname" 
                                                name="lname" 
                                                id="lname" 
                                                required 
                                                value={form.lastName} 
                                                onChange={(e) => handleForm(e, "lastName")} 
                                                onBlur={() => handleBlur("lastName")}
                                            />
                                            <div className={`invalid-lname-req invalid-feedback ${userDetailsStyles["invalid-feedback"]}`}>Last Name is required</div>
                                            <div className={`invalid-lname-other invalid-feedback ${userDetailsStyles["invalid-feedback"]}`}>Invalid last name</div>
                                        </div>
                                    </div>

                                    {/* Username and Email Row */}
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="userName" className={`form-label ${userDetailsStyles["form-label"]}`}>Username*</label>
                                            <input 
                                                type="text" 
                                                className={`form-control `} 
                                                placeholder="Enter username" 
                                                id="userName" 
                                                name="userName" 
                                                required 
                                                autoComplete="off" 
                                                value={form.userName} 
                                                onChange={(e) => handleForm(e, "userName")} 
                                                onBlur={() => handleBlur("userName")}
                                            />
                                            <div className={`invalid-userName-req invalid-feedback ${userDetailsStyles["invalid-feedback"]}`}>Username is required</div>
                                            <div className={`invalid-userName-other invalid-feedback ${userDetailsStyles["invalid-feedback"]}`}>Please enter a valid username</div>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="email" className={`form-label ${userDetailsStyles["form-label"]}`}>Email*</label>
                                            <input 
                                                type="email" 
                                                className={`form-control `} 
                                                placeholder="Enter email" 
                                                id="email" 
                                                name="email" 
                                                required 
                                                autoComplete="off" 
                                                value={form.email} 
                                                onChange={(e) => handleForm(e, "email")} 
                                                onBlur={() => handleBlur("email")}
                                            />
                                            <div className={`invalid-email-req invalid-feedback ${userDetailsStyles["invalid-feedback"]}`}>Email Address is required</div>
                                            <div className={`invalid-email-other invalid-feedback ${userDetailsStyles["invalid-feedback"]}`}>Please enter a valid email address e.g. john.doe@gmail.com</div>
                                        </div>
                                    </div>

                                    {/* Password and Confirm Password Row */}
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="password" className={`form-label ${userDetailsStyles["form-label"]}`}>Password*</label>
                                            <input 
                                                type="password"
                                                className={`form-control`}
                                                placeholder="Enter password" 
                                                id="password" 
                                                name="password" 
                                                required 
                                                autoComplete="off" 
                                                value={form.password} 
                                                onChange={(e) => handleForm(e, "password")} 
                                                onBlur={() => handleBlur("password")}
                                            />
                                            <div className={`invalid-password-req invalid-feedback ${userDetailsStyles["invalid-feedback"]}`}>Password is required</div>
                                            <div className={`invalid-password-other invalid-feedback ${userDetailsStyles["invalid-feedback"]}`}>Please enter a valid password</div>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="confirmPassword" className={`form-label ${userDetailsStyles["form-label"]}`}>Confirm Password*</label>
                                            <input 
                                                type="password" 
                                                className={`form-control`} 
                                                placeholder="Re-enter password" 
                                                id="confirmPassword" 
                                                name="confirmPassword" 
                                                required 
                                                autoComplete="off" 
                                                value={form.confirmPassword} 
                                                onChange={(e) => handleForm(e, "confirmPassword")} 
                                                onBlur={() => handleBlur("confirmPassword")}
                                            />
                                            <div className={`invalid-confirmPassword-req invalid-feedback ${userDetailsStyles["invalid-feedback"]}`}>Confirm password is required</div>
                                            <div className={`invalid-confirmPassword-other invalid-feedback ${userDetailsStyles["invalid-feedback"]}`}>Please enter a valid password</div>
                                            <div className={`invalid-confirmPassword-mismatch invalid-feedback ${userDetailsStyles["invalid-feedback"]}`}>Entered passwords do not match</div>
                                        </div>
                                    </div>

                                    {/* Branch Selection Row */}
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="branch-multiselect" className={`form-label ${userDetailsStyles["form-label"]}`}>Branch*</label>
                                            <MultiSelect
                                                id="branch-multiselect"
                                                value={selectedBranch}
                                                options={branches}
                                                optionLabel={optionLabelCallback}
                                                optionValue={optionValueCallback}
                                                onChange={multiSelectChangeCallback}
                                                onBlur={() => handleBlur("branchID")}
                                                placeholder={loadingBranches ? 'Loading branches...' : 'Select one or more branches'}
                                                disabled={loadingBranches}
                                                className="w-100 tg-multiselect"
                                                showSelectAll={true}
                                                selectAllLabel="Select All"
                                                maxSelectedLabels={2}
                                                virtualScroll
                                                scrollHeight="300px"
                                                panelClassName="tg-multiselect-panel"
                                            />
                                            {selectedBranch.length === 0 && <div className={`invalid-branch-req invalid-feedback ${userDetailsStyles["invalid-feedback"]}`}>Branch is required</div>}
                                            <div className={`invalid-branch-other invalid-feedback ${userDetailsStyles["invalid-feedback"]}`}>Please select a valid branch</div>
                                        </div>
                                    </div>
                                    
                                    {/* Submit Button Row */}
                                    <div className="row mt-2">
                                        <div className="col-md-6">
                                            <div id="loading-image" ref={loadingImageRef} style={{ display: "none", textAlign: "center" }}>
                                                Processing...<img height="35" width="35" src={"/images/loading-animation.gif"} alt="Loading" />
                                            </div>
                                            <button
                                                type="submit"
                                                className={`btn btn-info ${userDetailsStyles["bg-green"]}`}
                                                onClick={(e) => addUserFormSubmit(e)}
                                            >
                                                Submit
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}


export default AddUserModal;
